import './SmartTag.scss';

import { Node, mergeAttributes } from '@tiptap/core';
import type { IMenuItem } from '@dw/models/shared';
import { camelCaseToUserText, classNames, componentClassName } from '@dw/utils';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    smartTag: {
      /** Insert a smart tag at the current selection */
      insertSmartTag: (opt: IMenuItem) => ReturnType;
      /** Delete the currently selected smart tag (if selection is on one) */
      deleteSmartTag: () => ReturnType;
    };
  }
}

const isSmartComponent = (tagValue?: string): boolean => /^[A-Z]/.test((tagValue ?? '').replaceAll('{', ''));
const composeHumanReadableLabel = (tagValue?: string): string => {
  if (!tagValue) return '';
  return camelCaseToUserText(tagValue);
}
const wrapHandleBars = (tagValue?: string): string => {
  if (!tagValue) return '';
  if (/^{{/.test(tagValue)) return tagValue;

  return isSmartComponent(tagValue) ? `{{{${tagValue}}}}` : `{{${tagValue}}}`;
}

export const SmartTag = Node.create({
  name: 'smartTag',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      value: {
        default: '',
        parseHTML: el => el.getAttribute('data-value') ?? '',
        renderHTML: attrs => ({ 'data-value': attrs.value }),
      },
      label: {
        default: '',
        parseHTML: el => el.getAttribute('data-label'),
        renderHTML: attrs => (attrs.label ? { 'data-label': attrs.label } : {}),
      }
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-smarttag]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const display = node.attrs.value;
    const cn = componentClassName('smarttag-chip');

    return [
      'span',
      mergeAttributes(
        {
          'data-smarttag': '',
          'data-label': node.attrs.label,
          'data-value': node.attrs.value,
          contenteditable: 'false',
          class: classNames(cn(), {
            [cn('component')]: isSmartComponent(node.attrs.value),
          })
        },
        HTMLAttributes,
      ),
      display,
    ];
  },

  addNodeView() {
    // Vanilla NodeView (no React dependency). Runs only in the editor view.
    return ({ node }) => {
      const dom = document.createElement('span');

      dom.setAttribute('data-smarttag', '');
      if (node.attrs.label) dom.setAttribute('data-label', node.attrs.label);
      if (node.attrs.value) dom.setAttribute('data-value', node.attrs.value);

      const cn = componentClassName('smarttag-chip');
      dom.className = classNames(cn(), {
        [cn('component')]: isSmartComponent(node.attrs.value),
      });

      dom.contentEditable = 'false';
      // Show human-readable label in the editor:
      dom.textContent = node.attrs.label || '';
      return { dom };
    };
  },

  addCommands() {
    return {
      insertSmartTag:
        (opt: IMenuItem) =>
        ({ chain }) =>
          chain()
            .focus()
            .insertContent({
              type: this.name,
              attrs: {
                value: wrapHandleBars(opt.value),
                label: opt.text ?? composeHumanReadableLabel(opt.value)
              },
            })
            .run(),

      deleteSmartTag:
        () =>
        ({ state, dispatch }) => {
          const { from, to } = state.selection;
          let deleted = false;
          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === this.name) {
              if (dispatch) dispatch(state.tr.delete(pos, pos + node.nodeSize));
              deleted = true;
              return false;
            }
            return true;
          });
          return deleted;
        },
    };
  },

  addKeyboardShortcuts() {
    // Backspace/delete removes the whole tag if selection is on it.
    return {
      Backspace: ({ editor }) => {
        if (editor.isActive(this.name)) {
          return editor.commands.deleteSmartTag();
        }
        return false;
      },
      Delete: ({ editor }) => {
        if (editor.isActive(this.name)) {
          return editor.commands.deleteSmartTag();
        }
        return false;
      },
    };
  },
});
